import React, {useEffect, useRef, useState} from "react";
import API from "../helpers/Axios";

const normalizeStudios = (payload) => {
	if (Array.isArray(payload)) return payload;
	if (Array.isArray(payload?.data)) return payload.data;
	if (Array.isArray(payload?.items)) return payload.items;
	return [];
};

function AddFeaturedStudioModal({
	isOpen,
	isSaving = false,
	onClose,
	onSubmit,
	serverErrors = {},
	onClearServerErrors,
	initialValues = null,
	modalTitle = "Add featured studio",
	modalDescription = "Pin a studio to the featured list.",
	submitLabel = "Create featured studio",
	requireCover = false,
}) {
	const [search, setSearch] = useState("");
	const [studios, setStudios] = useState([]);
	const [selectedStudio, setSelectedStudio] = useState(null);
	const [imageFile, setImageFile] = useState(null);
	const [imageName, setImageName] = useState("");
	const [imagePreviewUrl, setImagePreviewUrl] = useState("");
	const [isSearching, setIsSearching] = useState(false);
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const [isActive, setIsActive] = useState(true);
	const [formError, setFormError] = useState("");
	const [imageError, setImageError] = useState("");
	const dropdownRef = useRef(null);
	const imagePreviewObjectUrlRef = useRef("");

	useEffect(() => {
		if (!isOpen) return;
		const presetStudioId = initialValues?.studio_id ?? initialValues?.studio?.id ?? null;
		const presetStudioName = initialValues?.studio?.name || initialValues?.studio_name || "";
		setSearch(presetStudioName);
		setStudios([]);
		setSelectedStudio(presetStudioId ? {id: presetStudioId, name: presetStudioName || `Studio #${presetStudioId}`} : null);
		setImageFile(null);
		setImageName("");
		if (imagePreviewObjectUrlRef.current) URL.revokeObjectURL(imagePreviewObjectUrlRef.current);
		imagePreviewObjectUrlRef.current = "";
		setImagePreviewUrl(initialValues?.cover_url || "");
		setIsSearching(false);
		setIsDropdownOpen(false);
		setIsActive(initialValues?.active ?? initialValues?.is_active ?? true);
		setFormError("");
		setImageError("");
	}, [isOpen, initialValues]);

	useEffect(() => () => {
		if (imagePreviewObjectUrlRef.current) URL.revokeObjectURL(imagePreviewObjectUrlRef.current);
	}, []);

	useEffect(() => {
		if (!isOpen) return;

		const handleClickOutside = (event) => {
			if (!dropdownRef.current) return;
			if (!dropdownRef.current.contains(event.target)) {
				setIsDropdownOpen(false);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [isOpen]);

	useEffect(() => {
		if (!isOpen) return;

		const timeoutId = setTimeout(async () => {
			setIsSearching(true);
			try {
				const res = await API.get("admin/studios", {
					params: {
						approved: 1,
						search: search.trim(),
						items_per_page: 8,
					},
				});
				setStudios(normalizeStudios(res.data));
			} catch (e) {
				setStudios([]);
			} finally {
				setIsSearching(false);
			}
		}, 250);

		return () => clearTimeout(timeoutId);
	}, [isOpen, search]);

	if (!isOpen) return null;

	const coverServerError = Array.isArray(serverErrors?.cover) ? serverErrors.cover[0] : "";

	const isAllowedImageFile = (file) => {
		if (!file) return false;
		const allowedMime = ["image/png", "image/jpeg"];
		if (allowedMime.includes(file.type)) return true;
		const lowerName = (file.name || "").toLowerCase();
		return lowerName.endsWith(".png") || lowerName.endsWith(".jpg") || lowerName.endsWith(".jpeg");
	};

	const handleSubmit = async () => {
		if (!selectedStudio?.id) {
			setFormError("Please select a studio.");
			return;
		}
		setFormError("");
		if (requireCover && !imageFile) {
			setImageError("Please upload an image.");
			return;
		}
		if (imageFile && !isAllowedImageFile(imageFile)) {
			setImageError("Only PNG or JPG images are allowed.");
			return;
		}

		setImageError("");
		await onSubmit({
			id: initialValues?.id,
			studio_id: selectedStudio.id,
			active: isActive,
			image: imageFile || null,
			sort_order: initialValues?.sort_order ?? 0,
		});
	};

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(0,0,0,0.45)] px-4">
			<div className="w-full max-w-[520px] rounded-xl bg-white p-6 shadow-[0_12px_32px_rgba(0,0,0,0.15)]">
				<div className="mb-5 flex items-start justify-between">
					<div>
						<h3 className="text-2xl font-semibold text-[#1E1F21]">{modalTitle}</h3>
						<p className="mt-1 text-sm text-[#6D6E6F]">{modalDescription}</p>
					</div>
					<button
						type="button"
						onClick={onClose}
						disabled={isSaving}
						className="rounded-md px-2 py-1 text-[#6D6E6F] hover:bg-[#F4F4F5] disabled:opacity-60"
					>
						✕
					</button>
				</div>

				<div className="space-y-4">
					<div ref={dropdownRef} className="relative">
						<label className="mb-2 block text-sm font-medium text-[#1E1F21]">Studio</label>
						<input
							type="text"
							value={search}
							onFocus={() => setIsDropdownOpen(true)}
							onChange={(e) => {
								setSearch(e.target.value);
								setSelectedStudio(null);
								setIsDropdownOpen(true);
								setFormError("");
								if (onClearServerErrors) onClearServerErrors();
							}}
							placeholder="Search studio name..."
							className="h-11 w-full rounded-md border border-[#DADADA] px-3 text-sm outline-none focus:border-[#31A762]"
						/>
						{isDropdownOpen ? (
							<div className="absolute left-0 right-0 top-full z-30 mt-2 max-h-56 overflow-auto rounded-md border border-[#E4E1E0] bg-white shadow-[0_10px_24px_rgba(0,0,0,0.12)]">
								{isSearching ? (
									<p className="px-3 py-2 text-sm text-[#6D6E6F]">Searching...</p>
								) : null}
								{!isSearching && studios.length === 0 ? (
									<p className="px-3 py-2 text-sm text-[#6D6E6F]">No studios found.</p>
								) : null}
								{!isSearching &&
									studios.map((studio) => (
										<button
											key={studio.id}
											type="button"
											onClick={() => {
												setSelectedStudio(studio);
												setSearch(studio.name || `Studio #${studio.id}`);
												setIsDropdownOpen(false);
												setFormError("");
												if (onClearServerErrors) onClearServerErrors();
											}}
											className="flex w-full items-center justify-between border-b border-[#F0F0F0] px-3 py-2 text-left last:border-b-0 hover:bg-[#F8F8F8]"
										>
											<div className="flex min-w-0 items-center gap-3">
												{studio.artwork_url ? (
													<img
														src={studio.artwork_url}
														alt={studio.name || `Studio ${studio.id}`}
														className="h-8 w-8 rounded-full object-cover"
													/>
												) : (
													<div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#EEF0F2] text-xs font-semibold text-[#6D6E6F]">
														{String(studio.name || "S").charAt(0).toUpperCase()}
													</div>
												)}
												<span className="truncate text-sm text-[#1E1F21]">{studio.name || `Studio #${studio.id}`}</span>
											</div>
											<span className="text-xs text-[#909090]">#{studio.id}</span>
										</button>
									))}
							</div>
						) : null}
					</div>

					<div className="flex items-center justify-between rounded-md border border-[#E4E1E0] px-3 py-2">
						<div>
							<p className="text-sm font-medium text-[#1E1F21]">Active</p>
							<p className="text-xs text-[#6D6E6F]">{isActive ? "Enabled" : "Disabled"}</p>
						</div>
						<button
							type="button"
							onClick={() => setIsActive((prev) => !prev)}
							className={`relative inline-flex h-7 w-12 items-center rounded-full transition ${
								isActive ? "bg-[#31A762]" : "bg-[#D1D5DB]"
							}`}
							aria-pressed={isActive}
						>
							<span
								className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${
									isActive ? "translate-x-6" : "translate-x-1"
								}`}
							/>
						</button>
					</div>

					<div>
						<label className="mb-2 block text-sm font-medium text-[#1E1F21]">Image (PNG/JPG)</label>
						<div className={`w-full rounded-md px-2 py-2 ${imageError || coverServerError ? "border border-[#DC3545]" : "border border-[#DADADA]"}`}>
							<div className="flex items-center gap-2">
								<label className="inline-flex cursor-pointer items-center rounded bg-[#EEF0F2] px-3 py-1.5 text-sm font-medium text-[#1E1F21] hover:bg-[#E5E7EB]">
									<input
										type="file"
										accept=".png,.jpg,.jpeg,image/png,image/jpeg"
										className="hidden"
										onChange={(e) => {
											const file = e.target.files?.[0] || null;
											setImageFile(file);
											setImageName(file ? file.name : "");
											if (imagePreviewObjectUrlRef.current) URL.revokeObjectURL(imagePreviewObjectUrlRef.current);
											if (file) {
												const nextPreview = URL.createObjectURL(file);
												imagePreviewObjectUrlRef.current = nextPreview;
												setImagePreviewUrl(nextPreview);
											} else {
												imagePreviewObjectUrlRef.current = "";
												setImagePreviewUrl(initialValues?.cover_url || "");
											}
											setFormError("");
											setImageError("");
											if (onClearServerErrors) onClearServerErrors();
										}}
									/>
									Choose image
								</label>
								<span className={`min-w-0 flex-1 truncate text-sm ${imageName ? "text-[#1E1F21]" : "text-[#6D6E6F]"}`}>
									{imageName || "PNG or JPG image"}
								</span>
							</div>
						</div>
							{imageError || coverServerError ? (
								<p className="mt-1 text-xs text-[#DC3545]">{imageError || coverServerError}</p>
							) : null}
							<p className="mt-1 text-xs text-[#6D6E6F]">Best display: 16:9 ratio, minimum width 1024px.</p>
							{imagePreviewUrl ? (
								<div className="mt-2 overflow-hidden rounded-md border border-[#E4E1E0] bg-[#F8F8F8]">
									<img src={imagePreviewUrl} alt="Featured studio preview" className="h-36 w-full object-cover" />
								</div>
							) : null}
							{!requireCover ? <p className="mt-1 text-xs text-[#6D6E6F]">Leave empty to keep current cover.</p> : null}
						</div>
					</div>

				{formError ? (
					<p className="mt-3 rounded-md bg-[#FBEAEC] px-3 py-2 text-sm text-[#842029]">{formError}</p>
				) : null}

				<div className="mt-6 flex justify-end gap-3">
					<button
						type="button"
						onClick={onClose}
						disabled={isSaving}
						className="rounded-md border border-[#D5D5D5] px-4 py-2 text-sm font-semibold text-[#1E1F21] disabled:opacity-60"
					>
						Cancel
					</button>
					<button
						type="button"
						onClick={handleSubmit}
						disabled={isSaving}
						className="rounded-md bg-[#31A762] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
					>
						{isSaving ? "Saving..." : submitLabel}
					</button>
				</div>
			</div>
		</div>
	);
}

export default AddFeaturedStudioModal;
